// Step 5d Phase 6 — Beta parallel-run console (admin-only, online-only).
// Four tabs instrument the four §10.2 measures. Desktop-weighted (a desk/admin tool) but
// doesn't break on a tablet. Reuses apiFetch + the ops-board theme; no new global state.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { C, F, M, fmtTimeInTz } from '../components/ops-board/theme';
import AppHeader from '../components/AppHeader';
import {
  fetchBases, fetchReconciliation, postReconEntry, completeReconDay,
  fetchConflicts, postObservedConflict, reviewObservedConflict,
  fetchMaintenanceSpells, fetchUsageMetrics, downloadCsv,
  MISS_REASON_CODES, OBSERVED_CONFLICT_REASON_CODES,
  type BetaBase, type ReconciliationView, type ConflictsView, type MaintenanceView, type UsageMetrics,
} from '../lib/beta';

const today = (): string => new Date().toISOString().slice(0, 10);
const fmtDateTime = (iso: string | null, tz?: string): string => {
  if (!iso) return '—';
  try { return tz ? `${iso.slice(0, 10)} ${fmtTimeInTz(iso, tz)}` : new Date(iso).toLocaleString(); } catch { return iso; }
};

type Tab = 'reconciliation' | 'conflicts' | 'maintenance' | 'usage';
const TABS: { key: Tab; label: string }[] = [
  { key: 'reconciliation', label: 'Reconciliation' },
  { key: 'conflicts', label: 'Conflicts' },
  { key: 'maintenance', label: 'Maintenance' },
  { key: 'usage', label: 'Usage' },
];

export default function BetaConsole() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('reconciliation');

  return (
    <div style={{ minHeight: '100vh', background: C.bg, fontFamily: F, color: C.text }}>
      <AppHeader context="Beta Instrumentation">
        <button onClick={() => navigate('/ops-board')} style={S.ghostBtn}>← Ops board</button>
      </AppHeader>

      <nav style={S.tabBar}>
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{ ...S.tab, ...(tab === t.key ? S.tabActive : {}) }}>{t.label}</button>
        ))}
      </nav>

      <main style={{ padding: 20, maxWidth: 1180, margin: '0 auto' }}>
        {tab === 'reconciliation' && <ReconciliationTab />}
        {tab === 'conflicts' && <ConflictsTab />}
        {tab === 'maintenance' && <MaintenanceTab />}
        {tab === 'usage' && <UsageTab />}
      </main>
    </div>
  );
}

// ── Shared bits ────────────────────────────────────────────────────────────────
function ExportBtn({ onClick }: { onClick: () => void }) {
  return <button onClick={onClick} style={S.exportBtn}>⬇ Export CSV</button>;
}
function Msg({ text, tone }: { text: string; tone: 'error' | 'info' }) {
  return <div style={{ ...S.msg, color: tone === 'error' ? C.red : C.textDim, background: tone === 'error' ? C.redLight : C.surfaceAlt }}>{text}</div>;
}

function useBases(): BetaBase[] {
  const [bases, setBases] = useState<BetaBase[]>([]);
  useEffect(() => { void fetchBases().then(setBases); }, []);
  return bases;
}

// ════════════════ MEASURE 1 — RECONCILIATION ════════════════
function ReconciliationTab() {
  const bases = useBases();
  const [date, setDate] = useState(today());
  const [baseId, setBaseId] = useState('');
  const [view, setView] = useState<ReconciliationView | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notFlownFor, setNotFlownFor] = useState<string | null>(null);

  useEffect(() => { if (!baseId && bases.length) setBaseId(bases[0].id); }, [bases, baseId]);

  const load = useCallback(async () => {
    if (!baseId) return;
    setBusy(true); setErr(null);
    const r = await fetchReconciliation(date, baseId);
    setBusy(false);
    if (r.error) { setErr(r.error); setView(null); } else setView(r.data);
  }, [date, baseId]);

  useEffect(() => { void load(); }, [load]);

  async function tick(taskId: string, outcome: 'matched' | 'in_avsuite_not_flown', reason_code?: string) {
    const r = await postReconEntry({ date, base_id: baseId, task_id: taskId, outcome, ...(reason_code ? { reason_code } : {}) });
    if (r.error) setErr(r.error); else { setNotFlownFor(null); void load(); }
  }
  async function complete() {
    const r = await completeReconDay(date, baseId);
    if (r.error) setErr(r.error); else void load();
  }
  const exportCsv = () => void downloadCsv(`/api/beta/reconciliation?date=${date}&base_id=${encodeURIComponent(baseId)}&format=csv`, `beta-reconciliation_${date}.csv`);

  const tz = view?.base.timezone;

  return (
    <section>
      <div style={S.toolbar}>
        <label style={S.lbl}>Date <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={S.input} /></label>
        <label style={S.lbl}>Base
          <select value={baseId} onChange={(e) => setBaseId(e.target.value)} style={S.input}>
            {bases.map((b) => <option key={b.id} value={b.id}>{b.short_code} — {b.name}</option>)}
          </select>
        </label>
        <button onClick={load} style={S.primaryBtn}>Load</button>
        <div style={{ flex: 1 }} />
        <ExportBtn onClick={exportCsv} />
      </div>

      {busy && <Msg text="Loading…" tone="info" />}
      {err && <Msg text={err} tone="error" />}

      {view && (
        <>
          <div style={S.dayBar}>
            <span>Day status: <strong style={{ color: view.day?.status === 'complete' ? C.green : C.amber }}>{view.day?.status ?? 'open'}</strong></span>
            {view.day?.reconciled_at && <span style={{ color: C.textDim, fontSize: 12 }}>completed {fmtDateTime(view.day.reconciled_at)}</span>}
            <div style={{ flex: 1 }} />
            <button onClick={complete} disabled={view.day?.status === 'complete'} style={S.primaryBtn}>Mark day complete</button>
          </div>

          <h3 style={S.h3}>AvSuite flights ({view.flights.length})</h3>
          <table style={S.table}>
            <thead><tr>{['Time', 'Aircraft', 'Crew', 'Status', 'Kind', 'Route', 'Reconciled', ''].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
            <tbody>
              {view.flights.map((f) => (
                <tr key={f.task_id} style={{ borderTop: `1px solid ${C.border}` }}>
                  <td style={S.td}>{f.block_start_at ? fmtTimeInTz(f.block_start_at, tz!) : '—'}</td>
                  <td style={{ ...S.td, fontFamily: M }}>{f.aircraft ?? '—'}</td>
                  <td style={S.td}>{f.crew ?? '—'}</td>
                  <td style={S.td}>{f.booking_status}</td>
                  <td style={S.td}>{f.kind ?? '—'}</td>
                  <td style={{ ...S.td, fontFamily: M }}>{f.route ?? '—'}</td>
                  <td style={S.td}>
                    {f.entry ? <span style={{ color: f.entry.outcome === 'matched' ? C.green : C.amber, fontWeight: 700 }}>
                      {f.entry.outcome}{f.entry.reason_code ? ` (${f.entry.reason_code})` : ''}</span> : <span style={{ color: C.textMuted }}>—</span>}
                  </td>
                  <td style={S.td}>
                    {notFlownFor === f.task_id ? (
                      <NotFlownPicker onConfirm={(code) => tick(f.task_id, 'in_avsuite_not_flown', code)} onCancel={() => setNotFlownFor(null)} />
                    ) : (
                      <span style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => tick(f.task_id, 'matched')} style={S.miniGreen}>✓ Flew</button>
                        <button onClick={() => setNotFlownFor(f.task_id)} style={S.miniAmber}>Didn't fly</button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {view.flights.length === 0 && <tr><td style={{ ...S.td, color: C.textMuted }} colSpan={8}>No non-cancelled flights for this base/day.</td></tr>}
            </tbody>
          </table>

          <AddMissingFlight date={date} baseId={baseId} onAdded={load} onError={setErr} />

          {view.misses.length > 0 && (
            <>
              <h3 style={S.h3}>Real flights missing from AvSuite ({view.misses.length})</h3>
              <table style={S.table}>
                <thead><tr>{['Reason', 'Real-system ref', 'Note', 'Recorded'].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {view.misses.map((m) => (
                    <tr key={m.id} style={{ borderTop: `1px solid ${C.border}` }}>
                      <td style={{ ...S.td, color: C.red, fontWeight: 700 }}>{m.reason_code ?? '—'}</td>
                      <td style={{ ...S.td, fontFamily: M }}>{m.external_ref ?? '—'}</td>
                      <td style={S.td}>{m.note ?? '—'}</td>
                      <td style={S.td}>{fmtDateTime(m.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </>
      )}
    </section>
  );
}

function NotFlownPicker({ onConfirm, onCancel }: { onConfirm: (code: string) => void; onCancel: () => void }) {
  const [code, setCode] = useState(MISS_REASON_CODES[0].code);
  return (
    <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <select value={code} onChange={(e) => setCode(e.target.value)} style={{ ...S.input, padding: '3px 6px' }}>
        {MISS_REASON_CODES.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
      </select>
      <button onClick={() => onConfirm(code)} style={S.miniAmber}>Confirm</button>
      <button onClick={onCancel} style={S.miniGhost}>✕</button>
    </span>
  );
}

function AddMissingFlight({ date, baseId, onAdded, onError }: { date: string; baseId: string; onAdded: () => void; onError: (e: string) => void }) {
  const [ref, setRef] = useState('');
  const [code, setCode] = useState(MISS_REASON_CODES[0].code);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  async function submit() {
    setBusy(true);
    const r = await postReconEntry({ date, base_id: baseId, task_id: null, outcome: 'missing_from_avsuite', reason_code: code, external_ref: ref || undefined, note: note || undefined });
    setBusy(false);
    if (r.error) onError(r.error); else { setRef(''); setNote(''); onAdded(); }
  }
  return (
    <div style={S.card}>
      <strong style={{ fontSize: 13 }}>Add a real flight missing from AvSuite</strong>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8, alignItems: 'flex-end' }}>
        <label style={S.lbl}>Real-system ref <input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="e.g. paper sheet #/rego+time" style={S.input} /></label>
        <label style={S.lbl}>Why missed
          <select value={code} onChange={(e) => setCode(e.target.value)} style={S.input}>
            {MISS_REASON_CODES.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
          </select>
        </label>
        <label style={{ ...S.lbl, flex: 1 }}>Note <input value={note} onChange={(e) => setNote(e.target.value)} style={S.input} /></label>
        <button onClick={submit} disabled={busy} style={S.primaryBtn}>Record miss</button>
      </div>
    </div>
  );
}

// ════════════════ MEASURE 2 — CONFLICTS ════════════════
function ConflictsTab() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [status, setStatus] = useState('');
  const [view, setView] = useState<ConflictsView | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setErr(null);
    const r = await fetchConflicts(from || undefined, to || undefined, status || undefined);
    if (r.error) { setErr(r.error); setView(null); } else setView(r.data);
  }, [from, to, status]);
  useEffect(() => { void load(); }, [load]);

  const exportCsv = () => {
    const qs = new URLSearchParams({ format: 'csv' });
    if (from) qs.set('from', from); if (to) qs.set('to', to); if (status) qs.set('status', status);
    void downloadCsv(`/api/beta/conflicts?${qs}`, 'beta-conflicts.csv');
  };

  return (
    <section>
      <div style={S.toolbar}>
        <label style={S.lbl}>From <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={S.input} /></label>
        <label style={S.lbl}>To <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={S.input} /></label>
        <label style={S.lbl}>Status
          <select value={status} onChange={(e) => setStatus(e.target.value)} style={S.input}>
            <option value="">all</option><option value="open">open</option><option value="reviewed">reviewed</option><option value="resolved">resolved</option>
          </select>
        </label>
        <button onClick={load} style={S.primaryBtn}>Load</button>
        <div style={{ flex: 1 }} />
        <ExportBtn onClick={exportCsv} />
      </div>
      {err && <Msg text={err} tone="error" />}

      <LogObservedConflict onAdded={load} onError={setErr} />

      {view && (
        <>
          <h3 style={S.h3}>Paper-caught conflicts AvSuite missed ({view.observed.length})</h3>
          <table style={S.table}>
            <thead><tr>{['Date', 'Description', 'Reason', 'Status', 'Recorded', ''].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
            <tbody>
              {view.observed.map((o) => (
                <tr key={o.id} style={{ borderTop: `1px solid ${C.border}` }}>
                  <td style={S.td}>{o.observed_date?.slice(0, 10) ?? '—'}</td>
                  <td style={S.td}>{o.description}</td>
                  <td style={S.td}>{o.reason_code ?? '—'}</td>
                  <td style={{ ...S.td, color: o.status === 'reviewed' ? C.green : C.amber, fontWeight: 700 }}>{o.status}</td>
                  <td style={S.td}>{fmtDateTime(o.created_at)}</td>
                  <td style={S.td}>{o.status === 'open' && <button onClick={async () => { const r = await reviewObservedConflict(o.id); if (r.error) setErr(r.error); else void load(); }} style={S.miniGreen}>Mark reviewed</button>}</td>
                </tr>
              ))}
              {view.observed.length === 0 && <tr><td style={{ ...S.td, color: C.textMuted }} colSpan={6}>None recorded.</td></tr>}
            </tbody>
          </table>

          <h3 style={S.h3}>AvSuite-detected conflict history ({view.automated.length})</h3>
          <table style={S.table}>
            <thead><tr>{['Created', 'Type', 'Status', 'Resolution', 'Task', 'Other task'].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
            <tbody>
              {view.automated.map((c) => (
                <tr key={c.id} style={{ borderTop: `1px solid ${C.border}` }}>
                  <td style={S.td}>{fmtDateTime(c.created_at)}</td>
                  <td style={S.td}>{c.type}</td>
                  <td style={{ ...S.td, color: c.status === 'resolved' ? C.green : C.amber, fontWeight: 700 }}>{c.status}</td>
                  <td style={S.td}>{c.resolution_code ?? '—'}</td>
                  <td style={{ ...S.td, fontFamily: M, fontSize: 11 }}>{c.task_id.slice(0, 8)}</td>
                  <td style={{ ...S.td, fontFamily: M, fontSize: 11 }}>{c.other_task_id ? c.other_task_id.slice(0, 8) : '—'}</td>
                </tr>
              ))}
              {view.automated.length === 0 && <tr><td style={{ ...S.td, color: C.textMuted }} colSpan={6}>None in range.</td></tr>}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}

function LogObservedConflict({ onAdded, onError }: { onAdded: () => void; onError: (e: string) => void }) {
  const [date, setDate] = useState(today());
  const [desc, setDesc] = useState('');
  const [code, setCode] = useState(OBSERVED_CONFLICT_REASON_CODES[0].code);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  async function submit() {
    if (!desc.trim()) { onError('Describe the conflict.'); return; }
    setBusy(true);
    const r = await postObservedConflict({ observed_date: date, description: desc, reason_code: code, note: note || undefined });
    setBusy(false);
    if (r.error) onError(r.error); else { setDesc(''); setNote(''); onAdded(); }
  }
  return (
    <div style={S.card}>
      <strong style={{ fontSize: 13 }}>Log a conflict AvSuite did not flag</strong>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8, alignItems: 'flex-end' }}>
        <label style={S.lbl}>Date <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={S.input} /></label>
        <label style={{ ...S.lbl, flex: 1, minWidth: 220 }}>Description <input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="What clashed?" style={S.input} /></label>
        <label style={S.lbl}>Why missed
          <select value={code} onChange={(e) => setCode(e.target.value)} style={S.input}>
            {OBSERVED_CONFLICT_REASON_CODES.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
          </select>
        </label>
        <label style={{ ...S.lbl, flex: 1 }}>Note <input value={note} onChange={(e) => setNote(e.target.value)} style={S.input} /></label>
        <button onClick={submit} disabled={busy} style={S.primaryBtn}>Log conflict</button>
      </div>
    </div>
  );
}

// ════════════════ MEASURE 3 — MAINTENANCE ════════════════
function MaintenanceTab() {
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState(today());
  const [view, setView] = useState<MaintenanceView | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setErr(null);
    const r = await fetchMaintenanceSpells(from, to);
    if (r.error) { setErr(r.error); setView(null); } else setView(r.data);
  }, [from, to]);
  useEffect(() => { void load(); }, [load]);
  const exportCsv = () => void downloadCsv(`/api/beta/maintenance-spells?from=${from}&to=${to}&format=csv`, 'beta-maintenance-spells.csv');

  return (
    <section>
      <div style={S.toolbar}>
        <label style={S.lbl}>From <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={S.input} /></label>
        <label style={S.lbl}>To <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={S.input} /></label>
        <button onClick={load} style={S.primaryBtn}>Load</button>
        <div style={{ flex: 1 }} />
        <ExportBtn onClick={exportCsv} />
      </div>
      {err && <Msg text={err} tone="error" />}
      {view && (
        <>
          <h3 style={S.h3}>U/S spells ({view.spells.length})</h3>
          <p style={{ color: C.textDim, fontSize: 12, margin: '0 0 8px' }}>Planned ECD and actual return are kept separate; effective end = LEAST(planned, actual return).</p>
          <table style={S.table}>
            <thead><tr>{['Aircraft', 'Base', 'U/S start', 'Planned ECD', 'Actual return', 'Effective end', 'Status'].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
            <tbody>
              {view.spells.map((s) => (
                <tr key={s.task_id} style={{ borderTop: `1px solid ${C.border}` }}>
                  <td style={{ ...S.td, fontFamily: M }}>{s.aircraft ?? '—'}</td>
                  <td style={S.td}>{s.base ?? '—'}</td>
                  <td style={S.td}>{fmtDateTime(s.us_start)}</td>
                  <td style={S.td}>{fmtDateTime(s.planned_ecd)}</td>
                  <td style={{ ...S.td, color: s.actual_return ? C.green : C.textMuted }}>{fmtDateTime(s.actual_return)}</td>
                  <td style={S.td}>{fmtDateTime(s.effective_end)}</td>
                  <td style={{ ...S.td, color: s.status === 'open' ? C.amber : C.green, fontWeight: 700 }}>{s.status}</td>
                </tr>
              ))}
              {view.spells.length === 0 && <tr><td style={{ ...S.td, color: C.textMuted }} colSpan={7}>No maintenance spells in range.</td></tr>}
            </tbody>
          </table>

          <h3 style={S.h3}>Current aircraft serviceability</h3>
          <table style={S.table}>
            <thead><tr>{['Aircraft', 'Serviceable', 'Status note', 'Expected return'].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
            <tbody>
              {view.aircraft_snapshot.map((a) => (
                <tr key={a.registration} style={{ borderTop: `1px solid ${C.border}` }}>
                  <td style={{ ...S.td, fontFamily: M }}>{a.registration}</td>
                  <td style={{ ...S.td, color: a.serviceable ? C.green : C.red, fontWeight: 700 }}>{a.serviceable ? 'yes' : 'no'}</td>
                  <td style={S.td}>{a.status_note ?? '—'}</td>
                  <td style={S.td}>{a.expected_return_date ? a.expected_return_date.slice(0, 10) : '—'}</td>
                </tr>
              ))}
              {view.aircraft_snapshot.length === 0 && <tr><td style={{ ...S.td, color: C.textMuted }} colSpan={4}>No aircraft in range.</td></tr>}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}

// ════════════════ MEASURE 4 — USAGE ════════════════
function UsageTab() {
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState(today());
  const [view, setView] = useState<UsageMetrics | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setErr(null);
    const r = await fetchUsageMetrics(from, to);
    if (r.error) { setErr(r.error); setView(null); } else setView(r.data);
  }, [from, to]);
  useEffect(() => { void load(); }, [load]);
  const exportCsv = () => void downloadCsv(`/api/beta/usage-metrics?from=${from}&to=${to}&format=csv`, 'beta-usage-metrics.csv');

  const maxBucket = useMemo(() => (view ? Math.max(1, ...view.buckets.map((b) => b.count)) : 1), [view]);

  return (
    <section>
      <div style={S.toolbar}>
        <label style={S.lbl}>From <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={S.input} /></label>
        <label style={S.lbl}>To <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={S.input} /></label>
        <button onClick={load} style={S.primaryBtn}>Load</button>
        <div style={{ flex: 1 }} />
        <ExportBtn onClick={exportCsv} />
      </div>
      {err && <Msg text={err} tone="error" />}
      {view && (
        <>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
            <Stat label="Offline-origin bookings" value={String(view.total)} />
            <Stat label="Created before flight" value={String(view.created_before_flight)} />
            <Stat label="% before flight" value={`${view.created_before_flight_pct}%`} accent />
          </div>
          <p style={{ color: C.textMuted, fontSize: 11, margin: '0 0 16px' }}>{view.boundary_note}</p>

          <h3 style={S.h3}>Lead-time distribution</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 560 }}>
            {view.buckets.map((b) => (
              <div key={b.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 140, fontSize: 12, color: C.textDim }}>{b.label}</span>
                <div style={{ flex: 1, background: C.surfaceAlt, borderRadius: 4, height: 18 }}>
                  <div style={{ width: `${(b.count / maxBucket) * 100}%`, background: C.accent, height: '100%', borderRadius: 4, minWidth: b.count ? 3 : 0 }} />
                </div>
                <span style={{ width: 28, textAlign: 'right', fontFamily: M, fontSize: 12 }}>{b.count}</span>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginTop: 20 }}>
            <BreakdownTable title="By base" rows={view.by_base.map((b) => ({ label: b.base, total: b.total, before: b.created_before, pct: b.pct }))} />
            <BreakdownTable title="By user" rows={view.by_user.map((u) => ({ label: u.user, total: u.total, before: u.created_before, pct: u.pct }))} />
          </div>
        </>
      )}
    </section>
  );
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div style={{ ...S.card, minWidth: 160 }}>
      <div style={{ fontSize: 12, color: C.textDim }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 800, color: accent ? C.accent : C.text, fontFamily: M }}>{value}</div>
    </div>
  );
}
function BreakdownTable({ title, rows }: { title: string; rows: { label: string; total: number; before: number; pct: number }[] }) {
  return (
    <div style={{ flex: 1, minWidth: 280 }}>
      <h3 style={S.h3}>{title}</h3>
      <table style={S.table}>
        <thead><tr>{['', 'Total', 'Before', '%'].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.label} style={{ borderTop: `1px solid ${C.border}` }}>
              <td style={S.td}>{r.label}</td><td style={{ ...S.td, fontFamily: M }}>{r.total}</td>
              <td style={{ ...S.td, fontFamily: M }}>{r.before}</td><td style={{ ...S.td, fontFamily: M, color: C.accent }}>{r.pct}%</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td style={{ ...S.td, color: C.textMuted }} colSpan={4}>None.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// ── styles ───────────────────────────────────────────────────────────────────
const S: Record<string, React.CSSProperties> = {
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', background: C.navy, color: '#fff' },
  betaPill: { background: C.amber, color: '#fff', fontSize: 10, fontWeight: 800, padding: '2px 6px', borderRadius: 4, letterSpacing: 0.5 },
  ghostBtn: { background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.85)', borderRadius: 5, padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: F },
  tabBar: { display: 'flex', gap: 2, padding: '0 20px', background: C.surface, borderBottom: `1px solid ${C.border}` },
  tab: { background: 'none', border: 'none', borderBottom: '3px solid transparent', padding: '12px 16px', fontSize: 13, fontWeight: 700, color: C.textDim, cursor: 'pointer', fontFamily: F },
  tabActive: { color: C.accent, borderBottom: `3px solid ${C.accent}` },
  toolbar: { display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 14 },
  lbl: { display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11, fontWeight: 700, color: C.textDim },
  input: { padding: '6px 8px', border: `1px solid ${C.borderMed}`, borderRadius: 5, fontSize: 13, fontFamily: F, background: '#fff', color: C.text },
  primaryBtn: { background: C.accent, color: '#fff', border: 'none', borderRadius: 5, padding: '7px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: F },
  exportBtn: { background: C.surface, color: C.accent, border: `1px solid ${C.accentBorder}`, borderRadius: 5, padding: '7px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: F },
  miniGreen: { background: C.greenLight, color: C.green, border: `1px solid ${C.greenBorder}`, borderRadius: 4, padding: '3px 8px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: F },
  miniAmber: { background: C.amberLight, color: C.amber, border: `1px solid ${C.amberBorder}`, borderRadius: 4, padding: '3px 8px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: F },
  miniGhost: { background: 'none', color: C.textMuted, border: `1px solid ${C.border}`, borderRadius: 4, padding: '3px 6px', fontSize: 11, cursor: 'pointer', fontFamily: F },
  dayBar: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6, marginBottom: 14, fontSize: 13 },
  h3: { fontSize: 14, fontWeight: 800, margin: '18px 0 8px', color: C.text },
  table: { width: '100%', borderCollapse: 'collapse', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 13 },
  th: { textAlign: 'left', padding: '8px 10px', fontSize: 11, fontWeight: 800, color: C.textDim, background: C.surfaceAlt, textTransform: 'uppercase', letterSpacing: 0.3 },
  td: { padding: '8px 10px', verticalAlign: 'middle' },
  card: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6, padding: 14, marginTop: 14 },
  msg: { padding: '8px 12px', borderRadius: 5, fontSize: 13, margin: '8px 0' },
};
